/**
 * 情景（背景）用テクスチャの手続き的生成。
 *
 * 「福岡に遊びに来ている感覚」を出すため、屋台から見上げた夜景を
 * Canvas で描き起こして Phaser のテクスチャにする。
 * 画像アセットを持たないので、都道府県テーマの色だけで雰囲気を差し替えられる。
 *
 * すべてオリジナルの図形描画であり、既存の写真・ロゴ・イラストは使用しない。
 */

import Phaser from 'phaser';

import type { ScenePhase } from '../config/types';

export const STEAM_TEXTURE_KEY = 'fx-steam';
export const GLOW_TEXTURE_KEY = 'fx-glow';
export const SKYLINE_TEXTURE_KEY = 'bg-skyline';
export const CITY_LIGHTS_TEXTURE_KEY = 'bg-citylights';
export const VIGNETTE_TEXTURE_KEY = 'bg-vignette';

export const skyTextureKey = (phase: ScenePhase): string => `bg-sky-${phase}`;

/**
 * 情景レイヤーの座標（テクスチャ内のローカル座標）。
 * 画面の左上を原点とする。
 */
export const SCENERY_LAYOUT = {
  /** 水平線（ここより上が空、下が博多湾）。器の口より少し上に置く */
  horizonY: 286,
  /** 水面の下端 */
  waterBottomY: 404,
  /** 遠景シルエットの高さ（下端が水平線に接する） */
  skylineHeight: 148,
} as const;

/** 観覧車の位置（遠景テクスチャ内のローカル座標）。 */
const FERRIS = { xRatio: 0.46, baseY: 96, radius: 30 } as const;

interface PhasePalette {
  skyTop: string;
  skyMid: string;
  skyLow: string;
  horizonGlow: string;
  water: string;
  waterDeep: string;
  /** 月（夕暮れは夕日）の色 */
  orb: string;
  orbY: number;
  /** 街の灯り・星の見え方（0〜1） */
  lightIntensity: number;
}

const PALETTES: Record<ScenePhase, PhasePalette> = {
  dusk: {
    skyTop: '#3f2b46',
    skyMid: '#8c4a51',
    skyLow: '#e08a52',
    horizonGlow: '#ffc27a',
    water: '#c9743f',
    waterDeep: '#4a2a2c',
    orb: '#ffd9a0',
    orbY: 244,
    lightIntensity: 0.28,
  },
  evening: {
    skyTop: '#1d1c3c',
    skyMid: '#4a2a55',
    skyLow: '#8d4a58',
    horizonGlow: '#e79a6a',
    water: '#8a5a6e',
    waterDeep: '#2a1f36',
    orb: '#ffeec4',
    orbY: 176,
    lightIntensity: 0.68,
  },
  night: {
    skyTop: '#0a1026',
    skyMid: '#131c3c',
    skyLow: '#28304f',
    horizonGlow: '#6f7cae',
    water: '#3d4a76',
    waterDeep: '#131a2e',
    orb: '#f6f2e2',
    orbY: 134,
    lightIntensity: 1,
  },
  deepnight: {
    skyTop: '#04060f',
    skyMid: '#0a1024',
    skyLow: '#141c38',
    horizonGlow: '#4a5788',
    water: '#243257',
    waterDeep: '#0a0e1c',
    orb: '#eef4ff',
    orbY: 86,
    lightIntensity: 1,
  },
  dawn: {
    skyTop: '#132a4e',
    skyMid: '#4a5c86',
    skyLow: '#d99a7c',
    horizonGlow: '#ffd9a8',
    water: '#a8809a',
    waterDeep: '#2b2440',
    orb: '#fff0c8',
    orbY: 268,
    lightIntensity: 0.42,
  },
};

/** 情景の進行順（夕暮れ → 夜明け）。 */
export const SCENE_PHASES: readonly ScenePhase[] = [
  'dusk',
  'evening',
  'night',
  'deepnight',
  'dawn',
];

/** フェーズの進行度（比較に使う）。 */
export function phaseIndex(phase: ScenePhase): number {
  return SCENE_PHASES.indexOf(phase);
}

/** 毎回同じ街並みになるようにした簡易疑似乱数（LCG）。 */
function createRandom(seed: number): () => number {
  let value = seed;
  return () => {
    value = (value * 1664525 + 1013904223) % 4294967296;
    return value / 4294967296;
  };
}

function canvas(
  scene: Phaser.Scene,
  key: string,
  width: number,
  height: number,
): CanvasRenderingContext2D | null {
  if (scene.textures.exists(key)) return null;
  const texture = scene.textures.createCanvas(key, width, height);
  if (!texture) return null;
  return texture.context;
}

function refresh(scene: Phaser.Scene, key: string): void {
  const texture = scene.textures.get(key);
  if (texture instanceof Phaser.Textures.CanvasTexture) texture.refresh();
}

/* ------------------------------ 空・海・月 ------------------------------ */

/** 時間帯ごとの空＋博多湾のテクスチャ。 */
export function createSkyTexture(
  scene: Phaser.Scene,
  phase: ScenePhase,
  width: number,
  height: number,
): void {
  const key = skyTextureKey(phase);
  const ctx = canvas(scene, key, width, height);
  if (!ctx) return;
  const palette = PALETTES[phase];
  const { horizonY, waterBottomY } = SCENERY_LAYOUT;

  // 空のグラデーション
  const sky = ctx.createLinearGradient(0, 0, 0, horizonY);
  sky.addColorStop(0, palette.skyTop);
  sky.addColorStop(0.55, palette.skyMid);
  sky.addColorStop(1, palette.skyLow);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, width, horizonY);

  // 水平線の光（空の明るさをやわらかく接地させる）
  const glow = ctx.createLinearGradient(0, horizonY - 46, 0, horizonY + 6);
  glow.addColorStop(0, `${palette.horizonGlow}00`);
  glow.addColorStop(1, `${palette.horizonGlow}66`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, horizonY - 46, width, 52);

  // 博多湾
  const water = ctx.createLinearGradient(0, horizonY, 0, waterBottomY);
  water.addColorStop(0, palette.water);
  water.addColorStop(1, palette.waterDeep);
  ctx.fillStyle = water;
  ctx.fillRect(0, horizonY, width, waterBottomY - horizonY);

  // 月（夕暮れは夕日）とその光暈
  const orbX = width * 0.72;
  const orbY = palette.orbY;
  const halo = ctx.createRadialGradient(orbX, orbY, 4, orbX, orbY, 92);
  halo.addColorStop(0, `${palette.orb}cc`);
  halo.addColorStop(0.28, `${palette.orb}33`);
  halo.addColorStop(1, `${palette.orb}00`);
  ctx.fillStyle = halo;
  ctx.fillRect(orbX - 100, orbY - 100, 200, 200);
  ctx.fillStyle = palette.orb;
  ctx.beginPath();
  ctx.arc(orbX, orbY, phase === 'dusk' || phase === 'dawn' ? 26 : 20, 0, Math.PI * 2);
  ctx.fill();

  // 月の光の柱（水面反射）
  const column = ctx.createLinearGradient(0, horizonY, 0, waterBottomY);
  column.addColorStop(0, `${palette.orb}55`);
  column.addColorStop(1, `${palette.orb}00`);
  ctx.fillStyle = column;
  ctx.fillRect(orbX - 26, horizonY, 52, waterBottomY - horizonY);

  // 薄い雲（横に流れる筋）
  ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
  const cloudRandom = createRandom(9721);
  for (let i = 0; i < 7; i += 1) {
    const cy = 40 + cloudRandom() * (horizonY - 120);
    const cw = 90 + cloudRandom() * 200;
    const ch = 6 + cloudRandom() * 10;
    ctx.beginPath();
    ctx.ellipse(cloudRandom() * width, cy, cw / 2, ch / 2, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // 大気の靄（フォトリアルな奥行き。空を少し白っぽくする）
  const haze = ctx.createLinearGradient(0, horizonY - 80, 0, horizonY);
  haze.addColorStop(0, `${palette.skyLow}00`);
  haze.addColorStop(1, `${palette.horizonGlow}22`);
  ctx.fillStyle = haze;
  ctx.fillRect(0, horizonY - 80, width, 80);

  // 水面より下（カウンター側の暗がり）
  const deep = ctx.createLinearGradient(0, waterBottomY, 0, height);
  deep.addColorStop(0, palette.waterDeep);
  deep.addColorStop(1, '#160f14');
  ctx.fillStyle = deep;
  ctx.fillRect(0, waterBottomY, width, height - waterBottomY);

  refresh(scene, key);
}

/* -------------------------------- 遠景の街 -------------------------------- */

/**
 * 遠景シルエット（山並み・ビル群・タワー・ドーム・観覧車）。
 * 空の色が変わっても使えるように、暗い単色のシルエットとして描く。
 */
export function createSkylineTexture(scene: Phaser.Scene, width: number): void {
  const height = SCENERY_LAYOUT.skylineHeight;
  const ctx = canvas(scene, SKYLINE_TEXTURE_KEY, width, height);
  if (!ctx) return;

  const random = createRandom(20250808);

  // 山並み（ビルより奥。薄く重ねて奥行きを出す）
  ctx.fillStyle = 'rgba(24, 20, 40, 0.55)';
  ctx.beginPath();
  ctx.moveTo(-10, height);
  const ridge = 6;
  for (let i = 0; i <= ridge; i += 1) {
    const rx = (width / ridge) * i;
    const ry = height - 74 - Math.sin(i * 1.7) * 26 - random() * 14;
    ctx.lineTo(rx, ry);
  }
  ctx.lineTo(width + 10, height);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = 'rgba(16, 12, 26, 0.9)';

  // ビル群
  let x = -12;
  while (x < width + 12) {
    const w = 16 + random() * 30;
    const h = 26 + random() * 62;
    ctx.fillRect(x, height - h, w, h);
    // 屋上の設備で輪郭に変化を付ける
    if (random() > 0.6) ctx.fillRect(x + w * 0.3, height - h - 8, w * 0.2, 8);
    x += w + 3 + random() * 10;
  }

  // タワー（先細りのシルエット）
  const towerX = width * 0.24;
  ctx.beginPath();
  ctx.moveTo(towerX - 17, height);
  ctx.lineTo(towerX - 5, height - 128);
  ctx.lineTo(towerX + 5, height - 128);
  ctx.lineTo(towerX + 17, height);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(towerX - 1.5, height - 148, 3, 22);

  // ドーム
  const domeX = width * 0.73;
  ctx.beginPath();
  ctx.arc(domeX, height - 22, 38, Math.PI, Math.PI * 2);
  ctx.closePath();
  ctx.fill();
  ctx.fillRect(domeX - 40, height - 24, 80, 24);

  // 観覧車（港の遊び場らしさ）
  const wheel = FERRIS;
  const wx = width * wheel.xRatio;
  const wy = height - wheel.baseY;
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(16, 12, 26, 0.9)';
  ctx.beginPath();
  ctx.arc(wx, wy, wheel.radius, 0, Math.PI * 2);
  ctx.stroke();
  for (let i = 0; i < 8; i += 1) {
    const a = (Math.PI * 2 * i) / 8;
    ctx.beginPath();
    ctx.moveTo(wx, wy);
    ctx.lineTo(wx + Math.cos(a) * wheel.radius, wy + Math.sin(a) * wheel.radius);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.moveTo(wx - 10, height);
  ctx.lineTo(wx, wy);
  ctx.lineTo(wx + 10, height);
  ctx.closePath();
  ctx.fill();

  refresh(scene, SKYLINE_TEXTURE_KEY);
}

/** 遠景の窓明かり。夜になるほど濃く重ねる。 */
export function createCityLightsTexture(scene: Phaser.Scene, width: number): void {
  const height = SCENERY_LAYOUT.skylineHeight;
  const ctx = canvas(scene, CITY_LIGHTS_TEXTURE_KEY, width, height);
  if (!ctx) return;

  const random = createRandom(31337);
  let x = -12;
  while (x < width + 12) {
    const w = 16 + random() * 30;
    const h = 26 + random() * 62;
    for (let wy = height - h + 6; wy < height - 6; wy += 7) {
      for (let wx = x + 3; wx < x + w - 3; wx += 6) {
        if (random() > 0.62) {
          ctx.fillStyle =
            random() > 0.25 ? 'rgba(255, 209, 138, 0.95)' : 'rgba(255, 246, 214, 0.8)';
          ctx.fillRect(wx, wy, 2, 3);
        }
      }
    }
    x += w + 3 + random() * 10;
  }

  // タワーとドームの明かり
  const towerX = width * 0.24;
  for (let i = 0; i < 9; i += 1) {
    ctx.fillStyle = i % 2 === 0 ? 'rgba(255, 196, 120, 0.95)' : 'rgba(180, 220, 255, 0.75)';
    ctx.fillRect(towerX - 2, height - 24 - i * 13, 4, 3);
  }
  const domeX = width * 0.73;
  ctx.fillStyle = 'rgba(210, 235, 255, 0.7)';
  for (let i = -3; i <= 3; i += 1) {
    ctx.fillRect(domeX + i * 11 - 1, height - 30, 2, 6);
  }

  // 観覧車のイルミネーション（縁に沿って並ぶ光の点）
  const wx = width * FERRIS.xRatio;
  const wy = height - FERRIS.baseY;
  for (let i = 0; i < 16; i += 1) {
    const a = (Math.PI * 2 * i) / 16;
    ctx.fillStyle = i % 3 === 0 ? 'rgba(255, 168, 196, 0.95)' : 'rgba(180, 224, 255, 0.9)';
    ctx.beginPath();
    ctx.arc(
      wx + Math.cos(a) * FERRIS.radius,
      wy + Math.sin(a) * FERRIS.radius,
      1.8,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  refresh(scene, CITY_LIGHTS_TEXTURE_KEY);
}

/* --------------------------- 手前を落とす暗がり --------------------------- */

/** 料理の視認性を守るため、下半分と四隅を落とすビネット。 */
export function createVignetteTexture(scene: Phaser.Scene, width: number, height: number): void {
  const ctx = canvas(scene, VIGNETTE_TEXTURE_KEY, width, height);
  if (!ctx) return;

  const bottom = ctx.createLinearGradient(0, height * 0.42, 0, height);
  bottom.addColorStop(0, 'rgba(10, 6, 12, 0)');
  bottom.addColorStop(0.55, 'rgba(10, 6, 12, 0.42)');
  bottom.addColorStop(1, 'rgba(8, 5, 10, 0.72)');
  ctx.fillStyle = bottom;
  ctx.fillRect(0, 0, width, height);

  const sides = ctx.createRadialGradient(
    width / 2,
    height * 0.4,
    width * 0.32,
    width / 2,
    height * 0.4,
    width * 0.86,
  );
  sides.addColorStop(0, 'rgba(0, 0, 0, 0)');
  sides.addColorStop(1, 'rgba(0, 0, 0, 0.4)');
  ctx.fillStyle = sides;
  ctx.fillRect(0, 0, width, height);

  refresh(scene, VIGNETTE_TEXTURE_KEY);
}

/* ---------------------------- 湯気・光の玉 ---------------------------- */

/** 立ち上る湯気（下が濃く、上へ薄く消える）。 */
export function createSteamTexture(scene: Phaser.Scene): void {
  const width = 56;
  const height = 128;
  const ctx = canvas(scene, STEAM_TEXTURE_KEY, width, height);
  if (!ctx) return;

  const puffs: Array<[number, number, number, number]> = [
    [0.5, 0.86, 0.4, 0.55],
    [0.38, 0.62, 0.33, 0.4],
    [0.62, 0.44, 0.29, 0.28],
    [0.46, 0.22, 0.24, 0.16],
  ];
  puffs.forEach(([px, py, pr, alpha]) => {
    const cx = width * px;
    const cy = height * py;
    const r = width * pr;
    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    gradient.addColorStop(0, `rgba(255, 252, 246, ${alpha})`);
    gradient.addColorStop(0.6, `rgba(255, 250, 242, ${alpha * 0.35})`);
    gradient.addColorStop(1, 'rgba(255, 250, 242, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
  });

  refresh(scene, STEAM_TEXTURE_KEY);
}

/** 提灯や水面のきらめきに使う柔らかい光。 */
export function createGlowTexture(scene: Phaser.Scene): void {
  const size = 64;
  const ctx = canvas(scene, GLOW_TEXTURE_KEY, size, size);
  if (!ctx) return;
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 0.95)');
  gradient.addColorStop(0.35, 'rgba(255, 255, 255, 0.34)');
  gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  refresh(scene, GLOW_TEXTURE_KEY);
}

/** 時間帯ごとの灯りの強さ（星・窓明かり・提灯に共通で使う）。 */
export function lightIntensityOf(phase: ScenePhase): number {
  return PALETTES[phase].lightIntensity;
}
